/**
 * Web Audio API stub — sintetiza SFX con osciladores hasta que conectemos
 * audio real. Cada metodo es seguro de llamar incluso antes de la primera
 * interaccion del usuario (no-op si el contexto aun esta suspendido).
 */
export class AudioSystem {
  constructor() {
    this.enabled = true;
    this.musicEnabled = false; // music muted in placeholder mode
    this.ctx = null;
    this.masterGain = null;
  }

  ensureContext() {
    if (this.ctx) return this.ctx;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      this.ctx = new Ctx();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.6;
      this.masterGain.connect(this.ctx.destination);
    } catch (err) {
      this.ctx = null;
    }
    return this.ctx;
  }

  /** Llama al primer gesto del usuario para desbloquear audio en iOS. */
  unlock() {
    const ctx = this.ensureContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  }

  setEnabled(on) {
    this.enabled = !!on;
    if (this.masterGain) {
      this.masterGain.gain.value = this.enabled ? 0.6 : 0;
    }
  }

  toggle() {
    this.setEnabled(!this.enabled);
    return this.enabled;
  }

  _beep({ freq = 440, type = 'square', duration = 0.1, volume = 0.3, attack = 0.005, freqEnd = null }) {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    if (!ctx || ctx.state === 'suspended') return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    if (freqEnd != null) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(20, freqEnd),
        ctx.currentTime + duration
      );
    }
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start();
    osc.stop(ctx.currentTime + duration + 0.02);
  }

  _noise({ duration = 0.1, volume = 0.25, filterFreq = 1000 }) {
    if (!this.enabled) return;
    const ctx = this.ensureContext();
    if (!ctx || ctx.state === 'suspended') return;
    const buffer = ctx.createBuffer(1, ctx.sampleRate * duration, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = filterFreq;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
    src.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    src.start();
  }

  // --- SFX ---
  shoot() { this._beep({ freq: 880, type: 'square', duration: 0.08, volume: 0.18, freqEnd: 220 }); }
  hit() { this._beep({ freq: 320, type: 'sawtooth', duration: 0.08, volume: 0.22, freqEnd: 80 }); }
  damage() { this._noise({ duration: 0.18, volume: 0.3, filterFreq: 600 }); }
  pickup() { this._beep({ freq: 660, type: 'triangle', duration: 0.15, volume: 0.22, freqEnd: 1320 }); }
  correct() {
    this._beep({ freq: 660, type: 'triangle', duration: 0.08, volume: 0.22 });
    setTimeout(() => this._beep({ freq: 990, type: 'triangle', duration: 0.12, volume: 0.22 }), 70);
  }
  wrong() {
    this._beep({ freq: 220, type: 'square', duration: 0.18, volume: 0.25, freqEnd: 120 });
  }
  levelUp() {
    [523, 659, 784, 1047].forEach((f, i) => {
      setTimeout(() => this._beep({ freq: f, type: 'triangle', duration: 0.12, volume: 0.22 }), i * 70);
    });
  }
  victory() {
    [523, 659, 784, 1047, 1319].forEach((f, i) => {
      setTimeout(() => this._beep({ freq: f, type: 'square', duration: 0.18, volume: 0.25 }), i * 110);
    });
  }
  gameOver() {
    [523, 415, 330, 220].forEach((f, i) => {
      setTimeout(() => this._beep({ freq: f, type: 'sawtooth', duration: 0.22, volume: 0.25 }), i * 130);
    });
  }
  bossRoar() {
    this._beep({ freq: 80, type: 'sawtooth', duration: 0.6, volume: 0.35, freqEnd: 40 });
    this._noise({ duration: 0.5, volume: 0.25, filterFreq: 200 });
  }
  crit() { this._beep({ freq: 1320, type: 'square', duration: 0.1, volume: 0.2, freqEnd: 660 }); }
}

let instance = null;
export function getAudio() {
  if (!instance) instance = new AudioSystem();
  return instance;
}
